import { GlobalAutoComplete } from "@oxygen/core";
import getThemeClasses from "@oxygen/theme";
import { CSSRelative } from "GlobalComponent/CSSCore";
import { Application } from "GlobalEnum/Application";
import {
  fireInteractiveTracking,
  fireNonInteractiveTracking,
} from "GlobalUtils/Analytics";
import { isNotNullOrEmptyArray } from "GlobalUtils/Common";
import { trackCustomData } from "GlobalUtils/cwTrackingPwa";
import { lockScroll, unlockScroll } from "GlobalUtils/ScrollLock";
import { getModelPageUrl } from "GlobalUtils/UrlFactory";
import PropTypes from "prop-types";
import React from "react";
import { withRouter } from "react-router-dom";
import { carModelApis } from "SrcPath/common/apis/carModel";
import { platform } from "SrcPath/enum/Platform";
import { globalSearchApis } from "SrcPath/GlobalSearch/apis";
import { TRENDING_MODELS_COUNT } from "SrcPath/GlobalSearch/constants";
import { debounce } from "throttle-debounce";
import { storage } from "../utils/Storage";
import { getSearchResultObject } from "./utils";

const propTypes = {
  /** Will render nothing if false */
  isActive: PropTypes.bool,
  /** Will hide global seach when user clicks outside of the search input box */
  handleBlackoutWindowClicked: PropTypes.func,
  platForm: PropTypes.object.isRequired,
  /** Specify icon position in search bar, it's default value is left */
  iconPosition: PropTypes.oneOf(["left", "right"]),
  /** Specify size, it's default value is sm */
  size: PropTypes.oneOf(["sm", "lg"]),
  /** The text that will be shown in input box, it's default value is Search */
  placeHolder: PropTypes.string,
  /** Ref to the search input box */
  inputRef: PropTypes.any,
};

const defaultProps = {
  isActive: false,
  platForm: platform.MOBILE,
  handleBlackoutWindowClicked: null,
  placeHolder: "Search",
  iconPosition: "left",
  size: "sm",
};
const containerClasses = getThemeClasses(`
  z-index-5;
`);
function getPlatformName(platForm) {
  if (platForm.id === platform.MOBILE.id) {
    return "Mobile";
  } else {
    return "Desktop";
  }
}
function getLocalStorageKey(platForm) {
  return `trendindCars_${platForm.id}_${Application.Carwale}`;
}
const TRACKING_CATEGORY = "GlobalSearch";
class GlobalSearch extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      text: "",
      results: [],
      trendingSearches: [],
      recentlyViewed: [],
      isMenuShown: true,
    };
    const { platForm } = this.props;
    this.localStorageTrendingKey = getLocalStorageKey(platForm);
  }
  handleBlur = () => {
    const { text } = this.state;
    const { platForm } = this.props;
    fireInteractiveTracking(
      `TopMenu-${getPlatformName(platForm)}`,
      "Global-Search-Outside-Click",
      text
    );
    this.blurTimer = setTimeout(() => {
      this.setState({
        text: "",
        results: [],
        isMenuShown: false,
      });
    }, 250); // This is done to execute click first and then blur action
  };
  handleFocus = () => {
    this.clearBlurTimer();
    this.setState({
      isMenuShown: true,
    });
    const { onFocus } = this.props;
    onFocus && onFocus();
  };

  handleResultsResponse = responses => {
    const { platForm } = this.props;
    const isMobile = platForm.id === platform.MOBILE.id;
    const searchResults = isNotNullOrEmptyArray(responses)
      ? responses.reduce((accumulator, item, index) => {
          accumulator.push(
            getSearchResultObject(item, isMobile, responses.length, index + 1)
          );
          return accumulator;
        }, [])
      : [];
    this.setState({
      results: searchResults,
    });
  };

  handleAutoCompleteQuery = query => {
    this.setState({
      text: query,
    });
    const { platForm, resultSize } = this.props;
    this.deboucedGlobalSearchApi(query, platForm, resultSize);
  };

  callGlobalSearchApi = (query, platForm, resultSize) => {
    globalSearchApis
      .fetchAutoCompletResults({
        query,
        resultSize: resultSize,
        showFeatured: true,
        isNcf: platForm.id === platform.MOBILE.id,
      })
      .then(this.handleResultsResponse)
      .catch(error => {
        console.error("failed in fetchAutoCompletResults", error);
      });
  };

  handleInputChange = event => {
    const query = event.target.value.trimLeft();
    if (query) {
      this.handleAutoCompleteQuery(query);
    } else {
      this.resetResults();
    }
  };

  resetResults = () => {
    this.setState({
      text: "",
      results: [],
    });
  };

  handleGlobalSearchInputClear = () => {
    this.resetResults();
  };

  closePopUp = () => {
    this.resetResults();
    const { platForm, handleBlackoutWindowClicked } = this.props;
    platForm.id === platform.MOBILE.id && unlockScroll();
    handleBlackoutWindowClicked && handleBlackoutWindowClicked();
  };
  handleBlackoutWindowClick = () => {
    const { platForm } = this.props;
    const { text } = this.state;
    fireInteractiveTracking(
      `TopMenu-${getPlatformName(platForm)}`,
      "Global-Search-Outside-Click",
      text
    );
    this.closePopUp();
  };
  handleBackIconClick = () => {
    const { text } = this.state;
    fireInteractiveTracking(
      "TopMenu-Mobile",
      "Global-Search-Back-Icon-Click",
      text
    );
    this.closePopUp();
  };

  handleTrendingSearchResultClick = (modelObj, event) => {
    if (modelObj && modelObj.makeMaskingName && modelObj.maskingName) {
      const { action, count, position } = event.currentTarget.dataset;
      fireInteractiveTracking(
        TRACKING_CATEGORY,
        action,
        `${modelObj.makeName}_${modelObj.modelName}_${count}_${position}`
      );
      trackCustomData(
        TRACKING_CATEGORY,
        action,
        `count=${count}|pos=${position}|result=${modelObj.makeName} ${modelObj.modelName}|modelid=${modelObj.modelId}`
      );
      const url = getModelPageUrl(
        modelObj.makeMaskingName,
        modelObj.maskingName
      );
      const { history } = this.props;
      history.push(url);
    }
    this.closePopUp();
  };

  handleUserHistoryResponse = results => {
    if (!results) {
      return;
    }
    const recentlyViewed = results.reduce((accumulator, item, index) => {
      if (item && item.makeName && item.modelName) {
        accumulator.push({
          label: `${item.makeName} ${item.modelName}`,
          onClick: this.handleTrendingSearchResultClick.bind(null, item),
          isFeatured: item.isSponsored,
          isUpcoming: item.isUpcoming,
          "data-action": "HistoryClick",
          "data-count": results.length,
          "data-position": index + 1,
        });
      }
      return accumulator;
    }, []);
    recentlyViewed.length > 0 &&
      fireNonInteractiveTracking(
        TRACKING_CATEGORY,
        "history_shown",
        `history_shown_${recentlyViewed.length}`
      );
    this.setState({
      recentlyViewed: [...recentlyViewed],
    });
  };

  fetchUserModelsHistory = () => {
    carModelApis
      .fetchUserModelsHistory()
      .then(this.handleUserHistoryResponse)
      .catch(err => {
        console.error(err);
      });
  };

  handleTrendingModelsResponse = response => {
    if (!(response && isNotNullOrEmptyArray(response.trendingCars))) {
      return;
    }
    const trendingSearches = response.trendingCars.reduce(
      (accumulator, trendingCar, index) => {
        if (trendingCar && trendingCar.makeName && trendingCar.modelName) {
          accumulator.push({
            label: `${trendingCar.makeName} ${trendingCar.modelName}`,
            isFeatured: false,
            onClick: this.handleTrendingSearchResultClick.bind(
              null,
              trendingCar
            ),
            isUpcoming: trendingCar.isUpcoming,
            "data-action": "TrendingClick",
            "data-count": response.trendingCars.length,
            "data-position": index + 1,
          });
        }
        return accumulator;
      },
      []
    );
    this.addSponsoredCampaign(response.sponsoredModel, trendingSearches);
    trendingSearches.slice(0, 5);
    trendingSearches.length > 0 &&
      fireNonInteractiveTracking(
        TRACKING_CATEGORY,
        "trending_shown",
        `trending_shown_${trendingSearches.length}`
      );
    this.setState({
      trendingSearches,
    });
  };

  addSponsoredCampaign = (sponsoredModel, result) => {
    if (sponsoredModel && sponsoredModel.makeName && sponsoredModel.modelName) {
      const sponsoredResult = {
        label: `${sponsoredModel.makeName} ${sponsoredModel.modelName}`,
        onClick: this.handleTrendingSearchResultClick.bind(
          null,
          sponsoredModel
        ),
        isFeatured: true,
        isUpcoming: sponsoredModel.isUpcoming,
        "data-action": "SponsoredClick",
        "data-position": sponsoredModel.adPosition,
        "data-count": result.length > 5 ? 5 : result.length, //trending cars has only max 5 cars but this is done because splicing of the final result is done here https://github.com/carwale/carwaleweb/blob/6d15e3c401818413aadacb85ac9a176985e0daf2/Carwale/PWA/src/GlobalSearch/GlobalSearch.js#L312.
      };
      fireNonInteractiveTracking(
        TRACKING_CATEGORY,
        "SponsoredImpression",
        `${sponsoredModel.makeName}_${sponsoredModel.modelName}_${result.length}_${sponsoredModel.adPosition}`
      );
      result.splice(sponsoredModel.adPosition - 1, 0, sponsoredResult);
    }
  };

  componentDidMount() {
    this.deboucedGlobalSearchApi = debounce(200, this.callGlobalSearchApi);
  }

  componentWillUnmount() {
    this.clearBlurTimer();
  }
  clearBlurTimer() {
    this.blurTimer && clearTimeout(this.blurTimer);
  }
  componentDidUpdate(prevProps) {
    const { isActive } = this.props;
    const { recentlyViewed, trendingSearches } = this.state;
    if (!prevProps.isActive && isActive) {
      const { platForm } = this.props;
      if (recentlyViewed.length < 1) {
        this.fetchUserModelsHistory();
      }
      if (!isNotNullOrEmptyArray(trendingSearches)) {
        const cachedTrendingSearchesResponse = storage.getSessionValue(
          this.localStorageTrendingKey
        );
        if (
          cachedTrendingSearchesResponse &&
          isNotNullOrEmptyArray(cachedTrendingSearchesResponse.trendingCars)
        ) {
          this.handleTrendingModelsResponse(cachedTrendingSearchesResponse);
        } else {
          carModelApis
            .fetchTrendingModels(TRENDING_MODELS_COUNT, platForm.id)
            .then(response => {
              this.handleTrendingModelsResponse(response);
              storage.setSessionValue(this.localStorageTrendingKey, response);
            })
            .catch(error => {
              console.error(error);
            });
        }
      }
    }
  }

  handleResultsClick = event => {
    const value = event.currentTarget.getAttribute("data-value");
    if (!value) {
      return;
    }
    const values = value.split("|");
    const {
      action,
      count,
      position,
      resultlabel,
      modelidlabel,
      islink,
    } = event.currentTarget.dataset;
    if (values.length) {
      fireInteractiveTracking(
        TRACKING_CATEGORY,
        action,
        `${count}_${position}_${this.state.text}_${resultlabel}`
      );
      trackCustomData(
        TRACKING_CATEGORY,
        action,
        `count=${count}|pos=${position}|term=${
          this.state.text
        }|result=${resultlabel}${modelidlabel ? `|${modelidlabel}` : ""}`
      );
      const [url] = values;
      if (islink === "true") {
        window.location.href = url;
      } else {
        const { history } = this.props;
        history.push(url);
        this.closePopUp();
      }
    }
  };
  static shouldShowMenu(state) {
    const { results, recentlyViewed, trendingSearches, isMenuShown } = state;
    return (
      isMenuShown &&
      (isNotNullOrEmptyArray(recentlyViewed) ||
        isNotNullOrEmptyArray(trendingSearches) ||
        isNotNullOrEmptyArray(results))
    );
  }
  render() {
    const {
      isActive,
      platForm,
      placeHolder,
      iconPosition,
      size,
      inputRef,
    } = this.props;
    const isMobile = platForm.id === platform.MOBILE.id;
    const { results, text, recentlyViewed, trendingSearches } = this.state;
    isActive && isMobile && lockScroll();
    if (isMobile && !isActive) {
      return null;
    }
    return (
      <CSSRelative className={containerClasses}>
        <GlobalAutoComplete
          data-testing-id="global-search"
          results={results}
          inputIcon={isMobile ? "arrow" : "search"}
          iconPosition={iconPosition}
          text={text}
          placeholder={placeHolder}
          size={size}
          isBlock
          isPopup={isMobile}
          isFocused={isMobile}
          inputRef={inputRef}
          onChange={this.handleInputChange}
          isMenuShown={GlobalSearch.shouldShowMenu(this.state)}
          onClear={this.handleGlobalSearchInputClear}
          handleBlackoutWindowClicked={this.handleBlackoutWindowClick}
          onFocus={isMobile ? null : this.handleFocus}
          onBlur={isMobile ? null : this.handleBlur}
          recentlyViewed={recentlyViewed}
          trendingSearches={trendingSearches}
          onIconClick={isMobile ? this.handleBackIconClick : null}
          handleClickOnResult={this.handleResultsClick}
        />
      </CSSRelative>
    );
  }
}

GlobalSearch.propTypes = propTypes;
GlobalSearch.defaultProps = defaultProps;

export default withRouter(GlobalSearch);
